import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ProductImage } from "@/components/inst/ProductImage";
import { ModelForm } from "@/components/inst/ModelForm";
import { MyModels } from "@/components/inst/MyModels";
import { LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function InstrumentationPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [models, setModels] = useState([]);
  const [pid, setPid] = useState(null);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadModels = () => api.get("/inst/my/models").then(({ data }) => setModels(data.models || []));
  useEffect(() => {
    Promise.all([api.get("/inst/my/products"), loadModels()])
      .then(([p]) => { setProducts(p.data.products || []); setPid(p.data.products?.[0]?.id || null); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const product = products.find((p) => p.id === pid);
  const openModel = (m) => { setPid(m.product_id); setEditing(m); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <header className="bg-[#0F172A] text-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <img src="/paslogo.png" alt="Peregrine" className="h-9 w-auto" />
          <div className="leading-tight">
            <div className="font-display font-extrabold text-sm tracking-tight">PEREGRINE INSTRUMENTATION</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Modeling Portal</div>
          </div>
          <span className="ml-auto text-sm text-zinc-300 hidden sm:block" data-testid="inst-portal-company">{user?.company_name}</span>
          <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="inst-portal-logout" title="Sign out" className="text-zinc-400 hover:text-[#EF4444]"><LogOut className="h-4 w-4" /></button>
        </div>
        <nav className="bg-[#1E293B] overflow-x-auto" data-testid="inst-product-tabs">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 flex gap-1 py-2">
            {products.map((p) => (
              <button key={p.id} onClick={() => { setPid(p.id); setEditing(null); }} data-testid={`inst-product-tab-${p.id}`}
                className={`px-4 py-1.5 text-xs whitespace-nowrap transition-colors ${pid === p.id ? "bg-[#D4AF37] text-[#0F172A] font-semibold" : "text-zinc-300 hover:bg-white/10"}`}>{p.code || p.name}</button>
            ))}
          </div>
        </nav>
      </header>
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10" data-testid="inst-portal-page">
        {loading ? <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div> : !product ? (
          <p className="text-center text-zinc-400 py-24" data-testid="inst-no-products">No products are available yet.</p>
        ) : (
          <motion.div key={product.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
            <ProductHero product={product} />
            <ModelForm key={`${product.id}-${editing?.id || "new"}`} product={product} editing={editing?.product_id === product.id ? editing : null}
              onSaved={(m) => { setEditing(m); loadModels(); }} onNew={() => setEditing(null)} />
          </motion.div>
        )}
        <MyModels models={models} onOpen={openModel} onChanged={loadModels} />
      </main>
    </div>
  );
}

function ProductHero({ product }) {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-5 gap-8" data-testid="inst-product-hero">
      <ProductImage product={product} className="lg:col-span-2 aspect-[4/3] bg-white" testid="inst-product-image" />
      <div className="lg:col-span-3">
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-[#0F172A]" data-testid="inst-product-title">{product.name}</h1>
        {(product.description || "").split("\n").filter(Boolean).map((para) => <p key={para} className="mt-3 text-sm text-zinc-600 leading-relaxed">{para}</p>)}
        {product.highlights_title && <h2 className="mt-6 font-display font-bold text-base md:text-lg text-[#0F172A]">{product.highlights_title}</h2>}
        <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-zinc-600 list-disc pl-5">
          {(product.highlights || []).map((h) => <li key={h}>{h}</li>)}
        </ul>
      </div>
    </section>
  );
}
